import './style.scss';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import PropTypes from 'prop-types';
import {
  getReviewerInfo,
  downloadPdfTemplate,
  downloadDocxTemplate,
} from 'apis/specializeReviews.api';

import ErrorDialog from 'components/ErrorDialog';
import { HugerteEditor } from 'components/FormControl';
import Loading from 'components/Loading';

import { TOAST } from 'constant';
import { toast } from 'layout';

import { isMemberAllowedReview } from 'utils/func';
import { validateGeneralInfoWithPayment } from 'utils/validation/generalInfo';
// eslint-disable-next-line import/no-cycle
import Signature from 'features/PrequalificationReview/components/Signature';
// eslint-disable-next-line import/no-cycle
import { DetailInfo, EvaluationForm, ExpenditureForm } from '..';

export default function CreateForm({
  isLoadingCreate,
  isErrorRes,
  errorRes,
  handleSubmit,
  onSubmit,
  control,
  watch,
  setValue,
  getValues,
  isDirty,
  dirtyFields,
  errors,
  isValid,
  isVisibleErrorDialog,
  setIsVisibleDialogError,
  dataGeneralProjectProposalInfo,
  projectProposalIdV2,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectProposalId, councilId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');
  const userId = searchParams.get('userId');
  const downloadTemplateButtonRef = useRef();
  // #region Data

  const generalProjectProposalInfo = useMemo(
    () => dataGeneralProjectProposalInfo?.data,
    [dataGeneralProjectProposalInfo]
  );

  // reviewer info
  const {
    data: dataReviewerInfo,
    isLoading: isLoadingReviewer,
    refetch: refetchReviewerInfo,
  } = useQuery(
    ['specialize-review', 'reviewer', userId, councilId, projectProposalId],
    ({ queryKey: [, , _userId, _councilId, _projectProposalId] }) =>
      getReviewerInfo(_userId, _councilId, _projectProposalId)
  );

  const reviewerInfo = useMemo(() => dataReviewerInfo?.data, [dataReviewerInfo]);

  // #end region Data

  // #region Events

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  // Wrapper onSubmit để validate chữ ký và thông tin chung trước khi save
  const handleSaveWithValidation = (data) => {
    // Kiểm tra thông tin chung
    const generalInfoError = validateGeneralInfoWithPayment(
      generalProjectProposalInfo,
      reviewerInfo,
      t
    );
    if (generalInfoError) {
      toast(TOAST.ERROR, generalInfoError);
      return;
    }

    // Kiểm tra chữ ký
    if (!reviewerInfo?.signatureUrl) {
      toast(TOAST.ERROR, t('council.review.signatureRequired'));
      return;
    }
    onSubmit(data);
  };

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateSpecializationReview', projectProposalIdV2, councilId, councilMemberId],
    () =>
      downloadPdfTemplate({
        projectProposalId: projectProposalIdV2,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M01 - Phieu nhan xet danh gia tham dinh chuyen mon.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateSpecializationReview', projectProposalIdV2, councilId, councilMemberId],
    () =>
      downloadDocxTemplate({
        projectProposalId: projectProposalIdV2,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M01 - Phieu nhan xet danh gia tham dinh chuyen mon.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  return (
    <div className="bg-white p-5">
      {(isLoadingCreate || isLoadingReviewer) && <Loading />}
      {isErrorRes && (
        <ErrorDialog
          title={t('council.review.create')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}

      <div className="flex justify-content-end">
        <Button
          label={t('council.review.downloadTemplate')}
          icon={`pi ${
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching ||
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file'
          }`}
          className="block w-auto h-3rem border-none ml-1"
          severity="info"
          onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
          disabled={
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching ||
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching
          }
        />
      </div>

      <Menu
        ref={downloadTemplateButtonRef}
        model={[
          {
            label: t('council.review.downloadPdfTemplate'),
            icon: 'pi pi-file-pdf',
            command: handleDownloadPdfTemplate,
            disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
          },
          {
            label: t('council.review.downloadDocxTemplate'),
            icon: 'pi pi-file-word',
            command: handleDownloadDocxTemplate,
            disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
          },
        ]}
        popup
      />

      <div className="p-fluid specialize-review-create">
        <div className="m-0 bg-white p-5 p-fluid">
          <h3 className="mb-5 text-4xl text-center">
            <span className="line-height-3 font-semibold">{t('council.m01.title')}</span>
            <br />
            <span className="font-normal">{t('council.m01.sciTechProject')}</span>
          </h3>
        </div>
        <DetailInfo
          generalProjectProposalInfo={generalProjectProposalInfo}
          reviewerInfo={reviewerInfo}
        />
        <form autoComplete="off" onSubmit={handleSubmit(handleSaveWithValidation)}>
          <div className="mx-8 relative">
            <h4>{t('council.m01.a.label')}</h4>
            <h5>{t('council.m01.a.a1.label')} </h5>
            <div className="mt-3 font-italic">{t('council.m01.a.a1.note')}</div>
            <div className="mt-3">
              <HugerteEditor
                name="reviewImportance"
                isRequired
                control={control}
                errors={errors}
                style={{ height: 'auto', minHeight: '300px' }}
              />
            </div>
            <h5>{t('council.m01.a.a2.label')} </h5>
            <div className="mt-3 font-italic">{t('council.m01.a.a2.note')}</div>
            <div className="mt-3">
              <HugerteEditor
                name="reviewQuality"
                isRequired
                control={control}
                errors={errors}
                style={{ height: 'auto', minHeight: '300px' }}
              />
            </div>
            <h5>{t('council.m01.a.a3.label')} </h5>
            <div className="mt-3 font-italic">{t('council.m01.a.a3.note')}</div>
            <div className="mt-3">
              <HugerteEditor
                name="reviewPicapability"
                isRequired
                control={control}
                errors={errors}
                style={{ height: 'auto', minHeight: '300px' }}
              />
            </div>
            <h5>{t('council.m01.a.a4.label')}</h5>
            <div className="mt-3 font-italic">{t('council.m01.a.a4.note')}</div>
            <ExpenditureForm
              setValue={setValue}
              getValues={getValues}
              control={control}
              errors={errors}
            />
            <h5>{t('council.m01.b.label')}</h5>
            <EvaluationForm
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              control={control}
              errors={errors}
            />
            <table className="form-table">
              <thead>
                <tr className="text-center">
                  <th>{t('council.m01.b.rank')}</th>
                  <th>{t('council.m01.b.totalPoint')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-center">
                  <td>I</td>
                  <td>{t('council.m01.b.rankI')}</td>
                </tr>
                <tr className="text-center">
                  <td>II</td>
                  <td>{t('council.m01.b.rankII')}</td>
                </tr>
                <tr className="text-center">
                  <td>III {t('council.m01.b.statusRankIII')}</td>
                  <td>{t('council.m01.b.rankIII')}</td>
                </tr>
              </tbody>
            </table>
            <h5>{t('council.m01.c.label')} </h5>
            <div className="mt-3">
              <HugerteEditor
                name="reviewConclusion"
                isRequired
                control={control}
                errors={errors}
                style={{ height: 'auto', minHeight: '300px' }}
              />
            </div>

            <div className="grid text-lg mt-4">
              <div className="col-7 px-8">
                <span>{t('council.m01.c.commitment')}</span>
              </div>
              <div className="col-5 text-center">
                <Signature refetchReviewerInfo={refetchReviewerInfo} reviewerInfo={reviewerInfo} />
              </div>
            </div>

            <div className="flex justify-content-end mt-8">
              <Button
                label={t('formLayout.action.close')}
                severity="danger"
                type="button"
                onClick={() => navigate(-1)}
                className="mr-2 w-8rem"
              />
              <Button
                label={t('formLayout.action.save')}
                severity="info"
                type="submit"
                className="w-8rem"
                onClick={checkValidation}
                disabled={
                  !(isDirty && Object.keys(dirtyFields).length) ||
                  !isMemberAllowedReview(reviewerInfo?.councilMembers?.[0])
                }
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  isLoadingCreate: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  isErrorRes: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  errorRes: PropTypes.shape({}),
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  dirtyFields: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  isValid: PropTypes.bool.isRequired,
  isVisibleErrorDialog: PropTypes.bool.isRequired,
  setIsVisibleDialogError: PropTypes.func.isRequired,
  dataGeneralProjectProposalInfo: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  projectProposalIdV2: PropTypes.string.isRequired,
};
